import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation } from "react-router-dom";
import { buildBreadcrumbSchema } from "../config/seo";
import styles from "./Breadcrumbs.module.css";

const formatLabel = (label) => {
  if (Array.isArray(label)) {
    return label
      .map((item, index) =>
        index === 0 ? item : item.charAt(0).toLowerCase() + item.slice(1),
      )
      .join(", ");
  }

  return label;
};

const SALE_FILTER_PARAM = "discounted";
const LEGACY_SALE_SORT_VALUE = "only-discounted";
const SALE_BREADCRUMB_LABEL = "Акційні товари";
const SALE_BREADCRUMB_PATH = "/promotions";

const getPathnameFromTarget = (target) => String(target || "").split(/[?#]/)[0];

const uppercaseFirst = (value = "") =>
  value ? value.charAt(0).toLocaleUpperCase("uk-UA") + value.slice(1) : "";

const normalizeBreadcrumbText = (value = "") =>
  String(value).trim().replace(/\s+/g, " ").toLocaleLowerCase("uk-UA");

const getAnimalQualifier = (label = "") => {
  const value = String(label || "").trim().replace(/\s+/g, " ");
  const match = value.match(/(?:^|\s)(для\s+.+)$/i);

  return match?.[1] || "";
};

const stripRepeatedAnimalQualifier = (label, previousLabel) => {
  const animalQualifier = getAnimalQualifier(previousLabel);
  if (!animalQualifier) return label;

  const value = String(label || "").trim();
  const normalizedValue = normalizeBreadcrumbText(value);
  const normalizedSuffix = ` ${normalizeBreadcrumbText(animalQualifier)}`;

  if (!normalizedValue.endsWith(normalizedSuffix)) return label;

  return value.slice(0, value.length - normalizedSuffix.length).trim();
};

const isCategoryKindPath = (target) => {
  const pathname = getPathnameFromTarget(target);
  const segments = pathname.split("/").filter(Boolean);

  return (
    (segments[0] === "category" && segments.length === 3) ||
    (segments[0] === "promotions" &&
      segments[1] === "category" &&
      segments.length === 4)
  );
};

const compactKindBreadcrumbLabels = (paths) =>
  paths.map((path, index) => {
    if (index === 0 || !isCategoryKindPath(path.to)) return path;

    const compactLabel = stripRepeatedAnimalQualifier(
      path.label,
      paths[index - 1]?.label,
    );

    return compactLabel === path.label
      ? path
      : {
          ...path,
          label: compactLabel,
        };
  });

const isSaleContextPath = (pathname = "") =>
  pathname === SALE_BREADCRUMB_PATH ||
  pathname.startsWith(`${SALE_BREADCRUMB_PATH}/`) ||
  pathname === "/sale";

const compactSaleBreadcrumbLabel = (path) => {
  const label = String(path?.label || "").trim();
  if (!label || label === SALE_BREADCRUMB_LABEL) return path;

  const categoryMatch = label.match(/^Акційні товари\s+(для\s+.+)$/i);
  if (categoryMatch) {
    return {
      ...path,
      label: uppercaseFirst(categoryMatch[1]),
    };
  }

  const kindMatch = label.match(/^Акційні товари:\s*(.+)$/i);
  if (kindMatch) {
    return {
      ...path,
      label: kindMatch[1],
    };
  }

  const legacyCategoryMatch = label.match(/^Акції\s+(для\s+.+)$/i);
  if (legacyCategoryMatch) {
    return {
      ...path,
      label: uppercaseFirst(legacyCategoryMatch[1]),
    };
  }

  return path;
};

const isSaleFilterEnabled = (search) => {
  const params = new URLSearchParams(search || "");
  const discountedValue = String(params.get(SALE_FILTER_PARAM) || "")
    .trim()
    .toLowerCase();
  const sortValue = String(params.get("sort") || "").trim();

  return (
    discountedValue === "1" ||
    discountedValue === "true" ||
    discountedValue === "yes" ||
    sortValue === LEGACY_SALE_SORT_VALUE
  );
};

const isSaleBreadcrumbPath = (path) => {
  const normalizedLabel = String(path?.label || "").trim().toLowerCase();
  if (normalizedLabel === SALE_BREADCRUMB_LABEL.toLowerCase()) return true;

  const target = String(path?.to || "");
  const queryPart = target.includes("?") ? target.split("?")[1] : "";
  if (!queryPart) return false;

  const params = new URLSearchParams(queryPart.split("#")[0]);
  const discountedValue = String(params.get(SALE_FILTER_PARAM) || "")
    .trim()
    .toLowerCase();
  const sortValue = String(params.get("sort") || "").trim();

  return (
    discountedValue === "1" ||
    discountedValue === "true" ||
    discountedValue === "yes" ||
    sortValue === LEGACY_SALE_SORT_VALUE
  );
};

const Breadcrumbs = ({ paths, mobileSingleLine = false }) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const breadcrumbsRef = useRef(null);
  const location = useLocation();
  const currentPath = `${location.pathname}${location.search}`;

  const preparedPaths = useMemo(
    () => {
      const normalizedPaths = paths.map((path) => ({
        ...path,
        label: formatLabel(path.label),
        to: path.to === "#" ? currentPath : path.to,
      }));
      const isSaleContext =
        isSaleContextPath(location.pathname) ||
        normalizedPaths.some((path) =>
          getPathnameFromTarget(path.to).startsWith(SALE_BREADCRUMB_PATH),
        );
      const compactedPaths = isSaleContext
        ? normalizedPaths.map(compactSaleBreadcrumbLabel)
        : normalizedPaths;
      const navigationPaths = compactKindBreadcrumbLabels(compactedPaths);

      const isProductsContext =
        location.pathname.startsWith("/products") ||
        location.pathname === "/promotions" ||
        location.pathname.startsWith("/promotions/") ||
        location.pathname === "/sale";
      const shouldShowSaleBreadcrumb =
        isProductsContext &&
        (location.pathname === "/promotions" ||
          location.pathname.startsWith("/promotions/") ||
          location.pathname === "/sale" ||
          isSaleFilterEnabled(location.search));

      if (!shouldShowSaleBreadcrumb) {
        return navigationPaths;
      }

      if (navigationPaths.some((path) => isSaleBreadcrumbPath(path))) {
        return navigationPaths;
      }

      const catalogIndex = navigationPaths.findIndex(
        (path) => getPathnameFromTarget(path.to) === "/products",
      );

      if (catalogIndex < 0) {
        return navigationPaths;
      }

      return [
        ...navigationPaths.slice(0, catalogIndex + 1),
        {
          label: SALE_BREADCRUMB_LABEL,
          to: SALE_BREADCRUMB_PATH,
        },
        ...navigationPaths.slice(catalogIndex + 1),
      ];
    },
    [paths, currentPath, location.pathname, location.search],
  );

  const breadcrumbSchema = useMemo(
    () =>
      buildBreadcrumbSchema(
        preparedPaths.map((path) => ({
          name: path.label,
          path: path.to,
        })),
      ),
    [preparedPaths],
  );

  useEffect(() => {
    const checkOverflow = () => {
      if (!breadcrumbsRef.current) return;
      setIsOverflowing(
        breadcrumbsRef.current.scrollWidth >
          breadcrumbsRef.current.clientWidth,
      );
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);

    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, [preparedPaths]);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <nav
        className={`${styles.breadcrumbsWrapper} ${
          mobileSingleLine ? styles.mobileSingleLine : ""
        }`.trim()}
        ref={breadcrumbsRef}
        aria-label="Хлібні крихти"
      >
        <ol className={styles.breadcrumbsInner}>
          {preparedPaths.map((path, index) => {
            const isLast = index === preparedPaths.length - 1;
            const isActive = path.to === currentPath || isLast;
            const shouldRenderLink = !isLast || path.clickable;

            return (
              <li
                key={`${path.label}-${path.to}`}
                className={styles.breadcrumbItem}
                aria-current={isActive ? "page" : undefined}
              >
                {index > 0 && (
                  <span className={styles.separator} aria-hidden="true">
                    ›
                  </span>
                )}
                {shouldRenderLink ? (
                  <Link
                    to={path.to}
                    title={path.label}
                    className={
                      isActive
                        ? `${styles.breadcrumbLink} ${styles.active}`
                        : styles.breadcrumbLink
                    }
                  >
                    {path.label}
                  </Link>
                ) : (
                  <span className={styles.current} title={path.label}>
                    {path.label}
                  </span>
                )}
              </li>
            );
          })}

          {!mobileSingleLine &&
            isOverflowing &&
            typeof window !== "undefined" &&
            window.innerWidth > 460 && (
              <li className={styles.separator} aria-hidden="true">
                ...
              </li>
            )}
        </ol>
      </nav>
    </>
  );
};

export default Breadcrumbs;
